import { useEffect, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  adminAnalytics,
  adminOrders,
  type DashboardStats,
  type SalesDataPoint,
  type TopProduct,
  type OrderAdmin,
} from '../../api/admin';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  IndianRupee,
  ShoppingCart,
  Users,
  Package,
  TrendingUp,
  Clock,
  ArrowUpRight,
  CheckCircle,
  Truck,
  XCircle,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const STATUS_OPTIONS = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'] as const;
const STATUS_CONFIG: Record<string, { bg: string; text: string; border: string }> = {
  pending:   { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/30' },
  confirmed: { bg: 'bg-blue-500/10',   text: 'text-blue-500',   border: 'border-blue-500/30' },
  shipped:   { bg: 'bg-purple-500/10',  text: 'text-purple-500', border: 'border-purple-500/30' },
  delivered: { bg: 'bg-emerald-500/10', text: 'text-emerald-500',border: 'border-emerald-500/30' },
  cancelled: { bg: 'bg-red-500/10',     text: 'text-red-500',    border: 'border-red-500/30' },
};

function StatCard({ label, value, icon: Icon, color, sub }: {
  label: string; value: string; icon: React.ElementType; color: string; sub?: string;
}) {
  const { isDark } = useTheme();
  return (
    <div className={`rounded-xl p-5 border transition-shadow hover:shadow-lg ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {sub && <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={20} className="text-white" />
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { isDark } = useTheme();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [chartData, setChartData] = useState<SalesDataPoint[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [recentOrders, setRecentOrders] = useState<OrderAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      adminAnalytics.dashboard().catch(() => null),
      adminAnalytics.salesChart('daily').catch(() => []),
      adminAnalytics.topProducts('month', 5).catch(() => []),
      adminOrders.list().catch(() => []),
    ]).then(([s, c, tp, allOrders]) => {
      setStats(s);
      setChartData(c);
      setTopProducts(tp);
      // Sort by date desc and take first 10
      const sorted = [...allOrders].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setRecentOrders(sorted.slice(0, 10));
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (orderId: string, status: string) => {
    setUpdatingId(orderId);
    try {
      await adminOrders.updateStatus(orderId, status);
      setRecentOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const fmt = (n: number) => `\u20B9${n.toLocaleString('en-IN')}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Overview of your store performance</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Total Revenue" value={fmt(stats?.total_revenue ?? 0)} icon={IndianRupee} color="bg-emerald-500" sub={`Avg order: ${fmt(stats?.avg_order_value ?? 0)}`} />
        <StatCard label="Total Orders" value={String(stats?.total_orders ?? 0)} icon={ShoppingCart} color="bg-blue-500" sub={`${stats?.pending_orders ?? 0} pending`} />
        <StatCard label="Customers" value={String(stats?.total_customers ?? 0)} icon={Users} color="bg-purple-500" />
        <StatCard label="Products" value={String(stats?.total_products ?? 0)} icon={Package} color="bg-amber-500" sub={`${(stats?.total_products ?? 0) - (stats?.low_stock_count ?? 0)} in stock`} />
      </div>

      {/* Recent Orders - Upper section */}
      <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold flex items-center gap-2">
            <Clock size={18} className="text-blue-500" />
            Recent Orders
          </h2>
          <a href="/admin/orders" className="text-xs text-emerald-500 hover:text-emerald-400 font-medium transition-colors">
            View all \u2192
          </a>
        </div>

        {recentOrders.length === 0 ? (
          <div className="text-center py-8">
            <Package size={36} className={`mx-auto mb-2 ${isDark ? 'text-gray-700' : 'text-gray-200'}`} />
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No orders yet</p>
          </div>
        ) : (
          <div className={`rounded-lg border overflow-x-auto ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <div className="min-w-[600px]">
            {/* Table header */}
            <div className={`grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider ${isDark ? 'bg-gray-800/50 text-gray-500' : 'bg-gray-50 text-gray-400'}`}>
              <div>Customer</div>
              <div>Date</div>
              <div>Status</div>
              <div className="text-right">Amount</div>
              <div></div>
            </div>

            {/* Order rows */}
            {recentOrders.map(order => {
              const cfg = STATUS_CONFIG[order.status] ?? STATUS_CONFIG.pending;
              const isExpanded = expandedOrderId === order.id;
              return (
                <div key={order.id}>
                  <div
                    className={`grid grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 px-4 py-3 items-center border-t transition-colors
                      ${isDark ? 'border-gray-800 hover:bg-gray-800/30' : 'border-gray-100 hover:bg-gray-50'}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{order.ship_first_name} {order.ship_last_name}</p>
                      <p className={`text-xs truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {order.ship_email} &middot; #{order.id.slice(0, 8)}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {new Date(order.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short',
                        })}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                        {new Date(order.created_at).toLocaleTimeString('en-IN', {
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div onClick={e => e.stopPropagation()}>
                      <select
                        value={order.status}
                        onChange={e => updateStatus(order.id, e.target.value)}
                        disabled={updatingId === order.id}
                        className={`px-2 py-1 rounded-md border text-xs font-semibold outline-none cursor-pointer transition-all
                          ${cfg.bg} ${cfg.text} ${cfg.border}
                          ${updatingId === order.id ? 'opacity-50' : ''}`}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold">{fmt(order.total)}</p>
                      <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{order.items?.length || 0} items</p>
                    </div>
                    <button
                      onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                      className={`p-1 rounded transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                    >
                      {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className={`px-4 pb-4 pt-2 border-t ${isDark ? 'border-gray-800 bg-gray-950/40' : 'border-gray-50 bg-gray-50/50'}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Customer Details
                          </p>
                          <div className={`space-y-1.5 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            <p className="font-medium">{order.ship_first_name} {order.ship_last_name}</p>
                            <p className="flex items-center gap-1.5 text-xs"><Mail size={11} /> {order.ship_email}</p>
                            <p className="flex items-center gap-1.5 text-xs"><Phone size={11} /> {order.ship_phone}</p>
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {order.ship_address}, {order.ship_city}, {order.ship_state} - {order.ship_pincode}
                            </p>
                          </div>
                        </div>
                        <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-gray-100 border border-gray-200'}`}>
                          <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Items
                          </p>
                          <div className="space-y-1">
                            {order.items?.map(item => (
                              <div key={item.id} className="flex justify-between text-xs">
                                <span>{item.product_name} × {item.quantity}</span>
                                <span className="font-medium">{fmt(item.unit_price * item.quantity)}</span>
                              </div>
                            ))}
                            {order.coupon_code && (
                              <p className={`text-xs mt-1 text-emerald-500`}>
                                Coupon: {order.coupon_code} (-{fmt(order.discount_amount ?? 0)})
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        )}
      </div>

      {/* Revenue chart */}
      <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <h2 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp size={18} className="text-emerald-500" />
          Revenue Trend (Last 30 Days)
        </h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#1f2937' : '#e5e7eb'} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: isDark ? '#6b7280' : '#9ca3af' }} />
              <YAxis tick={{ fontSize: 11, fill: isDark ? '#6b7280' : '#9ca3af' }} tickFormatter={v => `\u20B9${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{
                  backgroundColor: isDark ? '#111827' : '#fff',
                  border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  fontSize: 12,
                }}
                formatter={(value) => [`\u20B9${Number(value).toLocaleString('en-IN')}`, 'Revenue']}
              />
              <Area type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#revGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <ArrowUpRight size={18} className="text-emerald-500" />
            Top Products (This Month)
          </h2>
          <div className="space-y-3">
            {topProducts.length === 0 && <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No sales data yet</p>}
            {topProducts.map((p, i) => (
              <div key={p.product_id} className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-emerald-500 text-white' : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.product_name}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.brand}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold">{fmt(p.total_revenue)}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.total_sold} sold</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Status Summary */}
        <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <ShoppingCart size={18} className="text-blue-500" />
            Order Status Breakdown
          </h2>
          <div className="space-y-3">
            {STATUS_OPTIONS.map(s => {
              const count = recentOrders.filter(o => o.status === s).length;
              const cfg = STATUS_CONFIG[s];
              const icons: Record<string, React.ElementType> = {
                pending: Clock, confirmed: CheckCircle, shipped: Truck, delivered: CheckCircle, cancelled: XCircle,
              };
              const SIcon = icons[s] || Clock;
              return (
                <div key={s} className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                    <SIcon size={16} className={cfg.text} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium capitalize">{s}</p>
                  </div>
                  <span className={`text-lg font-bold ${cfg.text}`}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
